import {Component, Input, OnInit} from '@angular/core';
import {
  ApplicationCommercialEntity,
  ApplicationIndividual,
  booleanToYesNo,
  commercialEntityRoleType,
  individualRoleType,
} from '@portal-workspace/grow-shared-library';
import { MatDividerModule } from '@angular/material/divider';
import { CellComponent } from '../application-summary-component/cell.component';
import { FlexModule } from '@angular/flex-layout/flex';
import { NgTemplateOutlet, DatePipe } from '@angular/common';
import { EllipsisPipe } from '../../pipes/ellipsis.pipe';
import { DigitalIdService } from '../../../../../../apps/portal2/src/app/service/digital-id.service';

// Read-only party summary for the application-details tabs. Reads /get-application-parties rather
// than the party arrays on the hydrated application object: those come from the legacy projection,
// which collapses each individual's role flags to one string and orders entities by the position the
// wizard happened to send.
//
// Rendering is deliberately unchanged from the legacy version, so any visual difference is a defect
// in the new projection rather than an intended redesign. That is why the two legacy* shims are used
// to bucket instead of the role flags directly: a director who is also a shareholder still shows up
// once, under Director. Showing both is the point of the new shape, but it doubles the content on
// screen and is a decision for whoever owns this tab.
@Component({
    selector: 'individual-details',
    templateUrl: './individual-details.component.html',
    styleUrls: ['./individual-details.component.scss'],
    standalone: true,
    imports: [FlexModule, NgTemplateOutlet, CellComponent, MatDividerModule, DatePipe, EllipsisPipe]
})
export class IndividualDetailsComponent implements OnInit {

  booleanToYesNo = booleanToYesNo;

  @Input({required: true}) applicationId!: number;
  @Input({required: true}) type: 'all-non-guarantors' | 'guarantors-only' | 'spouse-only' = 'all-non-guarantors';

  hasEntries: boolean = false;

  applicants: ApplicationIndividual[] = [];
  directors: ApplicationIndividual[] = [];
  guarantors: ApplicationIndividual[] = [];
  partners: ApplicationIndividual[] = [];
  shareHolders: ApplicationIndividual[] = [];
  soleTraders: ApplicationIndividual[] = [];
  trustees: ApplicationIndividual[] = [];
  spouses: ApplicationIndividual[] = [];

  commonGuarantor:number = 0;
  commonNonGuarantor:number = 0;

  commercialTrustee: ApplicationCommercialEntity[]=[];
  commercialGuarantor: ApplicationCommercialEntity[]=[];
  commercialPartner: ApplicationCommercialEntity[]=[];

  constructor(private digitalIdService: DigitalIdService) {}

  ngOnInit(): void {
    this.digitalIdService.getApplicationPartiesFn(this.applicationId).subscribe(payload => {
      this.bucketIndividuals(payload.individuals);
      this.bucketCommercialEntities(payload.commercialEntities);
    });
  }

  private bucketIndividuals(individuals: ApplicationIndividual[]): void {
    const roles = individuals.map(individual => individualRoleType(individual));

    this.commonGuarantor = roles.filter(role => role === 'Guarantor').length;
    this.commonNonGuarantor = roles.filter(role => role !== 'Guarantor').length;

    individuals.forEach((ind, index) => {
      switch(roles[index]) {
        case 'Applicant':
          if (this.type === 'all-non-guarantors') {
            this.applicants.push(ind);
            this.hasEntries = true;
          }
          break;
        case 'Director':
          if (this.type === 'all-non-guarantors') {
            this.directors.push(ind);
            this.hasEntries = true;
          }
          break;
        case 'Guarantor':
          if (this.type === 'guarantors-only') {
            this.guarantors.push(ind);
            this.hasEntries = true;
          }
          break;
        case 'Partner':
          if (this.type === 'all-non-guarantors') {
            this.partners.push(ind);
            this.hasEntries = true;
          }
          break;
        case 'Shareholder':
          if (this.type === 'all-non-guarantors') {
            this.shareHolders.push(ind);
            this.hasEntries = true;
          }
          break;
        case 'SoleTrader':
          if (this.type === 'all-non-guarantors') {
            this.soleTraders.push(ind);
            this.hasEntries = true;
          }
          break
        case 'Trustee':
          if (this.type === 'all-non-guarantors') {
            this.trustees.push(ind);
            this.hasEntries = true;
          }
          break;
        case 'Spouse':
          if (this.type === 'spouse-only') {
            this.spouses.push(ind);
            this.hasEntries = true;
          }
          break;
      }
    });
  }

  private bucketCommercialEntities(commercialEntities: ApplicationCommercialEntity[]): void {
    // The legacy version dropped commercialEntities[0] on the assumption the primary borrower sits
    // first. It is excluded by flag here instead: sortOrder is the wizard's input position, so a
    // guarantor at index 0 used to hide the entity that followed it.
    commercialEntities.filter(entity => !entity.isPrimary).forEach(com => {
      switch(commercialEntityRoleType(com)){
        case 'Guarantor':
          if(this.type==='guarantors-only'){
            this.commercialGuarantor.push(com)
            this.hasEntries = true;
          }
          break;
        case 'Trustee':
          if(this.type==='all-non-guarantors'){
            this.commercialTrustee.push(com)
            this.hasEntries = true;
          }
          break;
        case 'Partner':
          if(this.type==='all-non-guarantors'){
            this.commercialPartner.push(com)
            this.hasEntries = true;
          }
          break;
      }
    })
  }

  isCommercialGuarantor(entity: ApplicationCommercialEntity): boolean {
    return commercialEntityRoleType(entity) === 'Guarantor';
  }

}
